import { Component, Input, OnInit } from '@angular/core';
import { formatDate } from '@app/functions/date-format';
import { capOnOptions, CapOnOptions } from '@app/modules/time-off-v3/types/carryover-card.type';
import { TitlecasePipe } from '@app/pipes';
import { OrdinalNumberPipe } from '@app/pipes/numbers/ordinal-number.pipe';
import { Locale, TranslatableKey } from '@app/types/translatable.type';
import {
    AccrualTimingDisplayOptions,
    TimeOffTypeUnitOfTimeDetailsHeaderDisplayOptions,
    TimeOffTypeUnitOfTimeDisplayOptions,
} from '@app/types/translatables/time-off-v3-display.options';
import { TimeOffPolicy } from '@models/time-off-v3/time-off-policy.model';
import { TimeOffSeniorityOverride } from '@models/time-off-v3/time-off-seniority-override.model';
import { TranslateService } from '@ngx-translate/core';
import { getTime } from '@time-off-v3/functions/time-unit-converter';
import {
    AccrualFrequencies,
    ANNUALLY,
    BIWEEKLY,
    DECIMAL_PLACES,
    DECIMAL_PLACES_FOR_PIPE,
    LAST_DAY_OF_THE_MONTH,
    MONTHLY,
    MonthsForDropdown,
    NUMBER_OF_DAYS_IN_MONTH,
    QUARTERLY,
    QuarterlyPeriods,
    SEMIMONTHLY,
    TimeOffDateFormatDashes,
    TimeOffDateFormatDayMonthDashes,
    TimeOffDateMonthDayYearDatePipeFormat,
    WEEKLY,
} from '@time-off-v3/meta/time-off-meta';
import { addWeeks } from 'date-fns';
import moment from 'moment';
import { TimeOffPreviews } from '../../../../interfaces/time-off-balance.interface';
import { TimeOffService } from '../../../../services';

@Component({
    selector: 'app-accrual-schedule-details-card',
    templateUrl: './accrual-schedule-details-card.template.html',
    styleUrls: ['./accrual-schedule-details-card.style.scss'],
})
export class AccrualScheduleDetailsCardComponent implements OnInit {
    @Input() timeOffPolicy = new TimeOffPolicy();
    @Input() editMode = false;

    AccrualTimingDisplayOptions = AccrualTimingDisplayOptions;

    accrualPreviews: TimeOffPreviews[] = [];
    capOnDateVal: CapOnOptions;
    capOnOptions = capOnOptions;
    timeOffSeniorityOverrides: TimeOffSeniorityOverride[] = [];
    titleCasePipe = new TitlecasePipe();
    ordinalNumberPipe = new OrdinalNumberPipe(this.translateService);
    decimalPlacesForPipe = DECIMAL_PLACES_FOR_PIPE;
    numberOfPreviews = 3;

    days = 0;
    accrualTiming: TranslatableKey = '';
    firstDayOfPeriod = '';
    biWeeklyAnchorDates = '';
    dayOfTheMonth = '';
    daysPerPeriod = 0;

    carryoverMethod: TranslatableKey = '';
    maxCarryoverDate: TranslatableKey = '';
    timeOffDateMonthDayYearDatePipeFormat = TimeOffDateMonthDayYearDatePipeFormat;

    constructor(
        private timeOffService: TimeOffService,
        private readonly translateService: TranslateService
    ) {}

    get unitOfTimeHeader(): string | TranslatableKey {
        const unit = this.timeOffPolicy?.timeOffType?.unitOfTimePlural;
        return TimeOffTypeUnitOfTimeDetailsHeaderDisplayOptions[unit] ?? unit + 'per year';
    }

    get unitOfTime(): string | TranslatableKey {
        const unit = this.timeOffPolicy?.timeOffType?.unitOfTimePlural;
        return TimeOffTypeUnitOfTimeDisplayOptions[unit] ?? unit;
    }

    formatDate = (date: Date): string => formatDate(date, 'shortNoYear', this.translateService.currentLang);

    ngOnInit(): void {
        this.timeOffSeniorityOverrides = this.timeOffPolicy.timeOffSeniorityOverrides;
        this.initialize();
    }

    initialize(): void {
        this.capOnDateVal = this.timeOffPolicy.getCapOnDateOption();

        this.getAccrualPreview(this.timeOffPolicy.accrualFrequency);
        this.setDaysPerYear();
        this.calculateDaysForPeriod();
        this.setDayOfPeriod();
        this.setCarryoverMethodAndMaxCarryOver();
    }

    calculateDaysForPeriod(): void {
        this.daysPerPeriod = 0;
        // Round to 3 decimal places because of business requirements.
        if (this.days !== null && this.timeOffPolicy.accrualFrequency) {
            const daysPerPeriod = this.days / AccrualFrequencies[this.timeOffPolicy.accrualFrequency];
            this.daysPerPeriod = +daysPerPeriod.toFixed(DECIMAL_PLACES);
        }
    }

    getAccrualPreview(frequency: string): void {
        if (this.timeOffPolicy.hasAccrualValuesSet(frequency)) {
            this.timeOffService.getAccrualPreview(this.timeOffPolicy).then((accrualPreviews: TimeOffPreviews[]) => {
                this.accrualPreviews = accrualPreviews.slice(0, this.numberOfPreviews);
            });
        }
    }

    getAccruesPerPeriod(seniorityOverride: TimeOffSeniorityOverride, timeOffPolicy: TimeOffPolicy): number {
        return getTime(
            AccrualFrequencies[timeOffPolicy.accrualFrequency] * seniorityOverride.daysAccruedPerPeriod,
            timeOffPolicy?.timeOffType
        );
    }

    getAccrualHappenOnDate(startDate: string, endDate: string): string {
        if (this.timeOffPolicy.accrualTiming === 'first-day') {
            return startDate;
        }

        if (this.timeOffPolicy.accrualTiming === 'last-day') {
            return endDate;
        }

        if (this.timeOffPolicy.accrualTiming === 'next-first-day') {
            return moment(endDate, TimeOffDateFormatDashes).add(1, 'days').format(TimeOffDateFormatDashes);
        }
    }

    private setDaysPerYear(): void {
        this.days = +getTime(
            +(this.timeOffPolicy.daysAccruedPerPeriod * AccrualFrequencies[this.timeOffPolicy.accrualFrequency]),
            this.timeOffPolicy.timeOffType
        ).toFixed(DECIMAL_PLACES);
    }

    // Logic taken from several components /app/modules/time-off-v3/components/monthly-fields
    // TODO: refactor to prevent code duplication, also too much code in a single function.
    private setDayOfPeriod(): void {
        switch (this.timeOffPolicy.accrualFrequency) {
            case WEEKLY:
                this.firstDayOfPeriod = this.translateService.instant(
                    `time-off-v3.policyComponents.accrualCard.${this.timeOffPolicy.startOfWeek}`
                );
                break;
            case BIWEEKLY:
                this.firstDayOfPeriod = this.translateService.instant(
                    `time-off-v3.policyComponents.accrualCard.${this.timeOffPolicy.startOfWeek}`
                );

                const firstDay = this.timeOffPolicy.weeklyFrequenciesAnchor;
                const secondDay = addWeeks(this.timeOffPolicy.weeklyFrequenciesAnchor, 2);

                this.biWeeklyAnchorDates = `${this.formatDate(firstDay)}, ${this.formatDate(secondDay)}...`;

                break;
            case SEMIMONTHLY:
                if (!this.timeOffPolicy.dayOfMonthOne || !this.timeOffPolicy.dayOfMonthTwo) {
                    break;
                }

                this.firstDayOfPeriod = this.translateService.instant('time-off-v3.semiMonthlyDateOption', {
                    firstDay: this.ordinalNumberPipe.transform(this.timeOffPolicy.dayOfMonthOne),
                    secondDay:
                        this.timeOffPolicy.dayOfMonthTwo > 0
                            ? this.ordinalNumberPipe.transform(this.timeOffPolicy.dayOfMonthTwo)
                            : this.translateService.instant('time-off-v3.lastDay'),
                });

                break;
            case MONTHLY:
                if (!this.timeOffPolicy.dayOfMonthOne) {
                    break;
                }

                this.firstDayOfPeriod = this.translateService.instant('time-off-v3.monthlyDateOption', {
                    day:
                        this.timeOffPolicy.dayOfMonthOne > 0
                            ? this.ordinalNumberPipe.transform(this.timeOffPolicy.dayOfMonthOne)
                            : this.translateService.instant('time-off-v3.lastDay'),
                });
                break;
            case QUARTERLY:
                if (!this.timeOffPolicy.dayOfYearThree && !this.timeOffPolicy.dayOfYearFour) {
                    this.firstDayOfPeriod = '';
                    this.dayOfTheMonth = '';
                    break;
                }

                const dayOfYearOne = moment(this.timeOffPolicy.dayOfYearOne, TimeOffDateFormatDashes).month();
                const dayOfYearTwo = moment(this.timeOffPolicy.dayOfYearTwo, TimeOffDateFormatDashes).month();
                const dayOfYearThree = moment(this.timeOffPolicy.dayOfYearThree, TimeOffDateFormatDashes).month();
                const dayOfYearFour = moment(this.timeOffPolicy.dayOfYearFour, TimeOffDateFormatDashes).month();

                this.firstDayOfPeriod = QuarterlyPeriods.find((period) => {
                    return (
                        dayOfYearOne === period.firstQuarter &&
                        dayOfYearTwo === period.secondQuarter &&
                        dayOfYearThree === period.thirdQuarter &&
                        dayOfYearFour === period.fourthQuarter
                    );
                }).label[this.translateService.currentLang as Locale];

                const dayForQuarter = this.getDayForQuarterlyPeriods();
                if (dayForQuarter !== undefined) {
                    this.dayOfTheMonth =
                        dayForQuarter > 0
                            ? this.ordinalNumberPipe.transform(dayForQuarter)
                            : this.translateService.instant('time-off-v3.lastDay');
                }

                break;
            case ANNUALLY:
                if (this.timeOffPolicy.accrualFromHireDate) {
                    this.firstDayOfPeriod = this.translateService.instant('time-off-v3.employeeHireDate');
                    break;
                }

                // initialized during creation flow and we don't have this value yet
                if (!this.timeOffPolicy.dayOfYearOne) {
                    break;
                }

                const month = moment(this.timeOffPolicy.dayOfYearOne, TimeOffDateFormatDashes).month();

                this.firstDayOfPeriod = MonthsForDropdown.find((monthItem) => {
                    return monthItem.value === month;
                }).label;

                const dayOfMonth = moment(this.timeOffPolicy.dayOfYearOne, TimeOffDateFormatDashes).date();

                this.dayOfTheMonth =
                    dayOfMonth > 0
                        ? this.ordinalNumberPipe.transform(dayOfMonth)
                        : this.translateService.instant('time-off-v3.lastDay');

                break;
        }
    }

    private getDayForQuarterlyPeriods(): number | undefined {
        // Using the dayOfYearThree because it should always be set for a quarterly accrual
        if (this.timeOffPolicy.dayOfYearThree) {
            const date: number = moment(this.timeOffPolicy.dayOfYearThree, TimeOffDateFormatDashes).date();
            return date > NUMBER_OF_DAYS_IN_MONTH ? LAST_DAY_OF_THE_MONTH : date;
        }
    }

    private setCarryoverMethodAndMaxCarryOver(): void {
        this.maxCarryoverDate = '';

        // for the first two possibilities, we won't show anymore carryover data
        if (this.timeOffPolicy.isAnnualReset()) {
            this.carryoverMethod = 'time-off-v3.policyComponents.carryover.annualReset';
            return;
        }

        if (this.timeOffPolicy.isUnlimitedCarryover()) {
            this.carryoverMethod = 'time-off-v3.policyComponents.carryover.unlimitedCarryover';
            return;
        }

        // For this option, we will show more carryover details
        this.carryoverMethod = 'time-off-v3.policyComponents.carryover.limitedCarryover';

        this.maxCarryoverDate = this.getMaxCarryOverDateTranslation();
    }

    /**
     * Logic borrowed from TimeOffRequestsCarryoverComponent.setCapOnDateDefault
     * TODO: refactor both places to prevent code duplication
     */
    private getMaxCarryOverDateTranslation(): string {
        if (
            (!this.timeOffPolicy.carryoverDate && !this.timeOffPolicy.carriesOverOnHireDate) ||
            moment(this.timeOffPolicy.carryoverDate).format(TimeOffDateFormatDayMonthDashes) ===
                moment().startOf('year').format(TimeOffDateFormatDayMonthDashes)
        ) {
            return 'time-off-v3.policyComponents.carryOverCard.maximumCarryoverAtTheEndOfTheYear';
        }

        if (!this.timeOffPolicy.carryoverDate && this.timeOffPolicy.carriesOverOnHireDate) {
            return 'time-off-v3.policyComponents.carryOverCard.maximumCarryoverOnEmployeeHireDate';
        }

        return 'time-off-v3.policyComponents.carryOverCard.maximumCarryoverOn';
    }
}
